import type { ISelectedItem } from '@/components/Atoms/FaceliftAccordion/FaceliftAccordion.types';
import { useClinicStore } from '@/store';

/**
 * Reusable helpers for clinic-based filtering.
 *
 * Usage examples:
 *  - Get default clinic IDs for API/global filters
 *  - Map DataTable global filters so that:
 *      * When user selects clinics → use those IDs
 *      * When user clears clinics / no filter → fall back to all user's clinics
 */
export const useClinicFilter = (clinicFilterKey: string, addSpaceInClinics = false) => {
	const clinics = useClinicStore((state) => state.currentClinics);

	// Simple derived IDs – no memo needed, very cheap
	const defaultClinicIds = clinics.map((clinic) => clinic.accountid);
	const defaultClinicNames = clinics.map((clinic) => addSpaceInClinics ? `${clinic.name} ` : clinic.name);

	/**
	 * Build filters for the backend:
	 * - Maps "Clinics" filter name to the given clinicFilterKey
	 * - Ensures that if Clinics is missing or empty, we still send all user's clinic IDs
	 */
	const mapGlobalFilters = (globalFilters: ISelectedItem[] = [], useClinicNames = false) => {
		//We need space for the clinic filter of Utilities
		const clinicIdsOrNames = useClinicNames ? defaultClinicNames.map((clinic) => addSpaceInClinics ? `${clinic} ` : clinic) : defaultClinicIds;
		const userClinicFilter = globalFilters.find(
			(filter) => filter.name === 'Clinics',
		);

		const mappedFilters = globalFilters.map((filter) => ({
			key: filter.key === 'Clinics' ? clinicFilterKey : filter.key,
			values: filter.options.map((option) => option.id as string),
		}));

		const shouldApplyDefaultClinics =
			(!userClinicFilter || userClinicFilter.options.length === 0) &&
			clinicIdsOrNames.length > 0;

		if (shouldApplyDefaultClinics) {
			mappedFilters.push({
				key: clinicFilterKey,
				values: clinicIdsOrNames,
			});
		}

		return mappedFilters;
	};

	/**
	 * Transform clinics into drawer filter items.
	 * Safe to call even if there are no clinics.
	 */
	const clinicFilterItems =
		clinics.length === 0
			? []
			: [
					{
						key: 'Clinics',
						name: 'Clinics',
						options: clinics.map((clinic) => ({
							label: clinic.name,
							id: clinic.accountid,
						})),
					},
				];

	/**
	 * Transform clinics into drawer filter items.
	 * Safe to call even if there are no clinics.
	 */
	const clinicFilterbyNameItems =
		clinics.length === 0
			? []
			: [
					{
						key: 'Clinics',
						name: 'Clinics',
						options: clinics.map((clinic) => ({
							label: clinic.name,
							id: clinic.name,
						})),
					},
				];

	return {
		clinics,
		defaultClinicIds,
		defaultClinicNames,
		mapGlobalFilters,
		clinicFilterItems,
		clinicFilterbyNameItems
	};
};
